import React, { useState, useMemo } from "react";
import { ReactComponent as IconSort } from "../../../assets/images/icon-sort.svg";
import { ReactComponent as ArrowRight } from "../../../assets/images/icon-arrow-right.svg";
import { ReactComponent as ArrowLeft } from "../../../assets/images/icon-arrow-left.svg";
import { ClientType } from "../../../store/slices/clientsSlice";
import { useNavigate } from "react-router-dom";
import { ContractType } from "../../../store/actions/accessCodeActions";
import { useSelector } from "react-redux";
import { selectState } from "../../../store/slices/themeSlice";
import useCustomStyles from "../../../hooks/useCustomStyles";
import { tableStyles } from "../../../assets/styles/ClientsStyles/tableStyles";
import { ErrorTriage } from "../../../api/awsApis";
import StatusDropdown from "./StatusDropdown";
import ColumnInfo from "../../../components/ColumnInfo";

interface TableColumn {
  key: string;
  label: string;
  /** Shown in an ⓘ tooltip next to the header label. */
  description?: string;
}

interface TableProps {
  columns: TableColumn[];
  data: Record<string, any>[];
  dropdowns?: {
    options: { value: string; key: string }[];
    initialState: string;
  }[];
  dropdownStatus?: {
    key: string;
    options: { value: string; label: string }[];
    onChange: (rowData: Record<string, any>, newValue: string) => void;
  }[];
  onRowClick?: (rowData: Record<string, any>) => void;
  handleNavigate?: (item: ClientType | ContractType | ErrorTriage) => void;
  link?: string;
  selectedTab?: "active" | "pending";
  // Pagination props
  pagination?: boolean;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  itemsPerPage?: number;
}

const TableComponent: React.FC<TableProps> = ({
  columns,
  data,
  onRowClick,
  handleNavigate,
  link,
  dropdownStatus,
  selectedTab,
  pagination = false,
  currentPage: externalCurrentPage,
  onPageChange,
  itemsPerPage: externalItemsPerPage = 10,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({
    key: "",
    direction: "asc",
  });
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const navigate = useNavigate();
  const { theme } = useSelector(selectState);
  const classes = useCustomStyles(tableStyles, theme);

  const currentPage = externalCurrentPage || localCurrentPage;
  const itemsPerPage = externalItemsPerPage;
  //  helper function to control table data colors
  const getStatusBadgeClass = (status: string, classes: any) => {
    if (!status) return classes.statusBadge;

    const normalizedStatus = status.toLowerCase().trim();

    if (normalizedStatus.includes("open")) {
      return classes.openStatusBadge;
    } else if (
      normalizedStatus.includes("progress") ||
      normalizedStatus.includes("on_progress")
    ) {
      return classes.progressStatusBadge;
    } else if (normalizedStatus.includes("resolved")) {
      return classes.resolvedStatusBadge;
    } else if (normalizedStatus.includes("inactive")) {
      return classes.inactiveStatusBadge;
    }

    return classes.statusBadge;
  };
  // Use useMemo to optimize sorting and filtering
  const sortedAndFilteredData = useMemo(() => {
    // First, sort the data
    const sorted = [...data].sort((a, b) => {
      if (sortConfig.key) {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (
          sortConfig.key === "created_at" &&
          a.created_at_raw &&
          b.created_at_raw
        ) {
          aValue = a.created_at_raw;
          bValue = b.created_at_raw;
        }
        if (
          sortConfig.key === "expire_at" &&
          a.expire_at_raw &&
          b.expire_at_raw
        ) {
          aValue = a.expire_at_raw;
          bValue = b.expire_at_raw;
        }
        if (
          sortConfig.key === "last_seen" &&
          a.last_seen_raw &&
          b.last_seen_raw
        ) {
          aValue = a.last_seen_raw;
          bValue = b.last_seen_raw;
        }
        // ✅ Normalize undefined/null
        if (aValue === undefined || aValue === null || "") return 1; // push aValue to end
        if (bValue === undefined || bValue === null || "") return -1; // push bValue to end

        // ✅ Handle numeric vs string sorting
        if (typeof aValue === "string" && typeof bValue === "string") {
          return sortConfig.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    // Then, filter the sorted data based on search term
    if (!searchTerm) return sorted;

    return sorted.filter((item) =>
      columns.some((col) =>
        item[col.key]
          ?.toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      )
    );
  }, [data, sortConfig, searchTerm, columns]);

  // Calculate pagination based on the FULL filtered dataset
  const totalPages = Math.ceil(sortedAndFilteredData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  // Only apply pagination for display, but search works on full dataset
  const currentItems = sortedAndFilteredData.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  // const handleRowClick = (rowData: Record<string, any>) => {
  //   if (onRowClick) {
  //     onRowClick(rowData);
  //   }

  //   if (handleNavigate) {
  //     if (rowData.client) {
  //       handleNavigate(rowData.client);
  //     } else if (rowData.contract) {
  //       handleNavigate(rowData.contract);
  //     } else if (rowData.errorApi) {
  //       handleNavigate(rowData.errorApi);
  //     } else if (rowData.advisor) {
  //       handleNavigate(rowData.advisor);
  //     }
  //   } else if (link) {
  //     navigate(link);
  //   } else {
  //     console.log("No navigation path provided.");
  //   }
  // };
  // Type guard functions
  const isErrorTriage = (obj: any): obj is ErrorTriage => {
    return obj && obj.processId !== undefined && obj.errorApi !== undefined;
  };

  const hasNestedClient = (obj: any): obj is { client: ClientType } => {
    return obj && obj.client !== undefined;
  };

  const hasNestedContract = (obj: any): obj is { contract: ContractType } => {
    return obj && obj.contract !== undefined;
  };

  const hasNestedAdvisor = (obj: any): obj is { advisor: any } => {
    return obj && obj.advisor !== undefined;
  };

  const handleRowClick = (rowData: Record<string, any>) => {
    if (onRowClick) {
      onRowClick(rowData);
    }

    if (handleNavigate) {
      if (isErrorTriage(rowData)) {
        handleNavigate(rowData); // Pass complete ErrorTriage
      } else if (hasNestedClient(rowData)) {
        handleNavigate(rowData.client);
      } else if (hasNestedContract(rowData)) {
        handleNavigate(rowData.contract);
      } else if (hasNestedAdvisor(rowData)) {
        handleNavigate(rowData.advisor);
      }
    } else if (link) {
      navigate(link);
    } else {
      console.log("No navigation path provided.");
    }
  };
  const handleSort = (key: string) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    // Reset to first page when sorting
    paginate(1);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    // Reset to first page when searching
    paginate(1);
  };

  const paginate = (pageNumber: number) => {
    if (onPageChange) {
      onPageChange(pageNumber);
    } else {
      setLocalCurrentPage(pageNumber);
    }
  };

  const displayData = pagination ? currentItems : sortedAndFilteredData;

  return (
    <div className={classes.container}>
      {/* Filter & Search */}
      <div className={classes.filterBar}>
        <input
          type="text"
          placeholder="Search"
          className={classes.searchInput}
          value={searchTerm}
          onChange={handleSearch}
        />
      </div>
      {/* Table */}
      <div className={classes.tableWrapper}>
        {/* Table */}
        <table className={classes.table}>
          <thead>
            <tr className={classes.tableHeader}>
              {columns.map(({ key, label, description }) => {
                const sampleValue = data.length > 0 ? data[0][key] : null;
                const isNumberColumn =
                  typeof sampleValue === "number" ||
                  (!isNaN(sampleValue) &&
                    sampleValue !== "" &&
                    sampleValue !== null);

                return (
                  <th
                    key={key}
                    className={classes.tableHeaderCell}
                    style={{
                      textAlign: isNumberColumn ? "center" : "left",
                      padding: "15px",
                      cursor: "pointer",
                      // width: "15%",
                      width:
                        selectedTab === "pending"
                          ? "20%"
                          : selectedTab === "active"
                          ? "18%"
                          : "18%",
                      ...(key === "verificationStatus" ? { width: "20%" } : {}),
                      fontSize: 14,
                      fontFamily: "Gilroy-SemiBold",
                      fontWeight: "400",
                    }}
                    onClick={() => handleSort(key)}
                  >
                    <div
                      style={{
                        justifyContent: isNumberColumn
                          ? "center"
                          : "flex-start",
                        display: "flex",
                        alignItems: "center",
                      }}
                      className={classes.headerContent}
                    >
                      <span className={classes.headerLabel}>{label}</span>
                      {description && <ColumnInfo label={label} description={description} />}
                      <IconSort width={20} height={20} />
                    </div>
                  </th>
                );
              })}
              <td className={`${classes.actionCell} ${classes.tableCell}`}>
                {/* <ArrowRight width={15} height={15} /> */}
              </td>
            </tr>
          </thead>
          <tbody>
            {displayData.map((item, index) => (
              <tr
                key={index}
                className={classes.tableRow}
                onClick={() => handleRowClick(item)}
              >
                {columns.map(({ key }) => {
                  if(key == 'status') console.log("Item status value:", item[key]);
                 // console.log("Rendering cell for key:", key, "with value:", item[key]);
                  const cellValue = item[key];
                  const isNumber =
                    typeof cellValue === "number" ||
                    (!isNaN(cellValue) &&
                      cellValue !== "" &&
                      cellValue !== null);

                  return (
                    <td
                      key={key}
                      className={`
               ${classes.tableCell}
                       ${isNumber ? classes.numberCell : ""}
               ${
                 key === "verificationStatus" || key === "status"
                   ? classes.statusCell
                   : ""
               }
               ${key === "email" ? classes.emailCell : ""}
                      `}
                    >
                      {dropdownStatus?.some(
                        (dropdown) => dropdown.key === key
                      ) ? (
                        <StatusDropdown
                          value={item[key]}
                          options={
                            dropdownStatus.find((d) => d.key === key)
                              ?.options || []
                          }
                          onChange={(newValue) => {
                            console.log("event", event);
                            const dropdownConfig = dropdownStatus?.find(
                              (d) => d.key === key
                            );
                            if (dropdownConfig) {
                              dropdownConfig.onChange(item, newValue);
                            }
                          }}
                        />
                      ) : key === "verificationStatus" || key === "status" ? (
                        item.codeType ? (
                          <span
                            className={
                              item[key] && item[key].includes("Inactive")
                                ? classes.inactiveStatusBadge
                                : classes.statusBadge
                            }
                          >
                            {cellValue}
                          </span>
                        ) : (
                          <span
                            className={
                              item[key] &&
                              item[key].toLowerCase().includes("open")
                                ? classes.openStatusBadge
                                : item[key] &&
                                  (item[key]
                                    .toLowerCase()
                                    .includes("progress") ||
                                    item[key]
                                      .toLowerCase()
                                      .includes("in_progress"))
                                ? classes.progressStatusBadge
                                : item[key] &&
                                  item[key].toLowerCase().includes("resolved")
                                ? classes.resolvedStatusBadge
                                : item[key] &&
                                  item[key].toLowerCase().includes("inactive")
                                ? classes.inactiveStatusBadge
                                : classes.statusBadge
                            }
                          >
                            {cellValue}
                          </span>
                        )
                      ) : (
                        item[key]
                      )}
                    </td>
                  );
                })}
                <td className={`${classes.actionCell} ${classes.tableCell}`}>
                  <ArrowRight width={15} height={15} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination Component */}
        {pagination && sortedAndFilteredData.length > itemsPerPage && (
          <div className={classes.paginationContainer}>
            {currentPage > 1 && (
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={classes.paginationButton}
              >
                <ArrowLeft width={15} height={15} />
              </button>
            )}
            <span className={classes.pageInfo}>
              Page {currentPage} of {totalPages}
            </span>
            {currentPage < totalPages && (
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={classes.paginationButton}
              >
                <ArrowRight width={15} height={15} />
              </button>
            )}
          </div>
        )}

        {/* Show message if no results found */}
        {sortedAndFilteredData.length === 0 && (
          <div className={classes.noResults}>
            No results found {searchTerm ? `for "${searchTerm}"` : ""}
          </div>
        )}
      </div>
    </div>
  );
};

export default TableComponent;
